/**
 * Reference to a regular page opened with pushAsync().
 * The __brand field is a phantom type for compile-time type safety.
 *
 * When using `returnCallback` with pushAsync(), the callback receives the result R
 * when the page calls returnAsync(). However, if the user closes the page via the
 * header back button (without explicit save/cancel), the callback receives `undefined`.
 *
 * Callback implementations MUST handle undefined results:
 * ```typescript
 * onSomeResult(_context: unknown, result: SomeResult) {
 *   // if page was closed without explicit action (e.g. header back button), ignore
 *   if (!result) return;
 *   // ... handle result
 * }
 * ```
 */
export interface PageRef<P = unknown, R = void> {
  readonly pageName: string;
  readonly __params?: P;
  readonly __result?: R;
  readonly __brand?: 'PageRef';
}

/**
 * Reference to a callable page opened with callAsync().
 * Callable pages return a Promise, and WHEN it resolves is load-bearing: not when
 * the page calls returnAsync(), but when the page's component is destroyed — see
 * NavController.deliverCallResultOnPageDestroy. returnAsync only writes the result
 * down. Resolving at returnAsync time is the #4411 bug: it hands the answer to a
 * caller that is still marked as having left, and a caller that does any work
 * before checking that mark throws the answer away.
 * The __brand field is a phantom type for compile-time type safety.
 */
export interface CallablePageRef<P = unknown, R = void> {
  readonly pageName: string;
  readonly __params?: P;
  readonly __result?: R;
  readonly __brand?: 'CallablePageRef';
}

export type PageParams<T> = T extends PageRef<infer P, any> ? P : T extends CallablePageRef<infer P, any> ? P : never;

export type PageResult<T> = T extends PageRef<any, infer R> ? R : T extends CallablePageRef<any, infer R> ? R : never;

export function isPageRef(value: any): value is PageRef<any, any> {
  return value && typeof value === 'object' && typeof value.pageName === 'string';
}

export function isCallablePageRef(value: any): value is CallablePageRef<any, any> {
  return value && typeof value === 'object' && typeof value.pageName === 'string';
}
